/**
 * Props Preparation for Runtime Guarantees
 *
 * Prepares props for foundation components with:
 * - Param defaults from runtime schema
 * - Guaranteed content structure (no null checks needed)
 * - Field defaults applied to `content.data` items from the bound schemas
 *
 * This enables simpler component code by ensuring predictable prop shapes.
 */

import { isRichSchema } from '@uniweb/core'

/**
 * Guarantee item has flat content structure
 *
 * @param {Object} item - Raw item from parser
 * @returns {Object} Item with guaranteed flat structure
 */
function guaranteeItemStructure(item) {
  return {
    title: item.title || '',
    pretitle: item.pretitle || '',
    subtitle: item.subtitle || '',
    paragraphs: item.paragraphs || [],
    links: item.links || [],
    images: item.images || [],
    lists: item.lists || [],
    icons: item.icons || [],
    videos: item.videos || [],
    snippets: item.snippets || [],
    buttons: item.buttons || [],
    data: item.data || {},
    cards: item.cards || [],
    documents: item.documents || [],
    forms: item.forms || [],
    quotes: item.quotes || [],
    headings: item.headings || [],
    ...(item.math && item.math.length ? { math: item.math } : {}),
  }
}

/**
 * Guarantee content structure exists
 * Returns a flat content object with all standard fields guaranteed to exist
 *
 * @param {Object} parsedContent - Raw parsed content from semantic parser (flat structure)
 * @returns {Object} Content with guaranteed flat structure
 */
export function guaranteeContentStructure(parsedContent) {
  const content = parsedContent || {}

  return {
    // Flat header fields
    title: content.title || '',
    pretitle: content.pretitle || '',
    subtitle: content.subtitle || '',
    alignment: content.alignment || null,

    // Flat body fields
    paragraphs: content.paragraphs || [],
    links: content.links || [],
    images: content.images || [],
    lists: content.lists || [],
    icons: content.icons || [],
    videos: content.videos || [],
    insets: content.insets || [],
    snippets: content.snippets || [],
    buttons: content.buttons || [],
    data: content.data || {},
    cards: content.cards || [],
    documents: content.documents || [],
    forms: content.forms || [],
    quotes: content.quotes || [],
    headings: content.headings || [],

    // Rare collections — surfaced only when present so pages that don't
    // use them don't pay the allocation cost. Foundations that need them
    // should check for presence (content.math?.length) or use
    // content.sequence for in-order rendering.
    ...(content.math && content.math.length ? { math: content.math } : {}),

    // Items with guaranteed structure
    items: (content.items || []).map(guaranteeItemStructure),

    // Sequence for ordered rendering
    sequence: content.sequence || [],

    // Preserve raw content if present
    raw: content.raw,
  }
}

/**
 * Apply a schema to a single object
 * Only processes fields defined in the schema, preserves unknown fields
 *
 * @param {Object} obj - The object to process
 * @param {Object} schema - Schema definition (fieldName -> fieldDef)
 * @returns {Object} Object with schema defaults applied
 */
function applySchemaToObject(obj, schema) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return obj
  }

  const result = { ...obj }

  for (const [field, fieldDef] of Object.entries(schema)) {
    // Get the default value - handle both shorthand and full form
    const defaultValue = typeof fieldDef === 'object' ? fieldDef.default : undefined

    // Apply default if field is missing and default exists
    if (result[field] === undefined && defaultValue !== undefined) {
      result[field] = defaultValue
    }

    // Bare type strings ('string', 'decimal', …) carry nothing more to apply.
    if (typeof fieldDef !== 'object') continue

    // Inline picklist (`enum`): if the value is set but not among the allowed
    // values, fall back to the default.
    if (Array.isArray(fieldDef.enum)) {
      if (result[field] !== undefined && !fieldDef.enum.includes(result[field]) && defaultValue !== undefined) {
        result[field] = defaultValue
      }
    }

    // Nested object → recurse into its field map.
    if (fieldDef.type === 'object' && fieldDef.fields && result[field]) {
      result[field] = applySchemaToObject(result[field], fieldDef.fields)
    }

    // Array of objects → apply the element field map to each item.
    if (fieldDef.type === 'array' && fieldDef.items && Array.isArray(result[field])) {
      const items = fieldDef.items
      if (items && typeof items === 'object' && items.type === 'object' && items.fields) {
        result[field] = result[field].map((item) => applySchemaToObject(item, items.fields))
      }
    }
  }

  return result
}

/**
 * Apply a schema to a value (object or array of objects)
 *
 * @param {Object|Array} value - The value to process
 * @param {Object} schema - Schema definition
 * @returns {Object|Array} Value with schema defaults applied
 */
function applySchemaToValue(value, schema) {
  if (Array.isArray(value)) {
    return value.map(item => applySchemaToObject(item, schema))
  }
  return applySchemaToObject(value, schema)
}

/**
 * Apply field defaults from a rich form `fields` array to an object.
 *
 * Recurses into `type: 'form'` (composite arrays with childSchema) and
 * `type: 'nestedObject'` / `type: 'object'` (single nested objects).
 *
 * Conditional visibility (`field.condition`) is not yet applied here —
 * components receive all fields the author filled plus defaults; hiding
 * is a later pass that requires the shared evaluateCondition util.
 *
 * @param {Object} obj - Row data (object keyed by field id)
 * @param {Array} fields - Rich field definitions
 * @returns {Object} - obj with defaults filled in
 */
function applyRichFieldDefaults(obj, fields) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj
  if (!Array.isArray(fields)) return obj

  const result = { ...obj }

  for (const field of fields) {
    if (!field || typeof field !== 'object' || !field.id) continue
    const id = field.id

    if (result[id] === undefined && field.default !== undefined) {
      result[id] = field.default
    }

    if (field.type === 'form' && field.childSchema && Array.isArray(result[id])) {
      result[id] = result[id].map(item =>
        applyRichFieldDefaults(item, field.childSchema.fields)
      )
    } else if (
      (field.type === 'nestedObject' || field.type === 'object') &&
      Array.isArray(field.fields) &&
      result[id] &&
      typeof result[id] === 'object'
    ) {
      result[id] = applyRichFieldDefaults(result[id], field.fields)
    }
  }

  return result
}

/**
 * Apply a rich form schema to its stored value.
 *
 * Shape rules:
 *   - composite (isComposite=true) → value is array of childSchema rows
 *     - when `childRecords` is set, value may be `{ [childRecords]: [...] }`
 *   - non-composite → value is a single object keyed by field id
 */
function applyRichSchemaToValue(value, schema) {
  if (value == null) return value

  if (schema.isComposite && schema.childSchema) {
    const childFields = schema.childSchema.fields
    const queryKey = schema.childRecords

    if (queryKey && value && typeof value === 'object' && !Array.isArray(value)) {
      const arr = Array.isArray(value[queryKey]) ? value[queryKey] : []
      return {
        ...value,
        [queryKey]: arr.map(row => applyRichFieldDefaults(row, childFields)),
      }
    }

    if (Array.isArray(value)) {
      return value.map(row => applyRichFieldDefaults(row, childFields))
    }

    return value
  }

  if (Array.isArray(schema.fields)) {
    return applyRichFieldDefaults(value, schema.fields)
  }

  return value
}

/**
 * Apply schemas to content.data
 * Only processes tags that have a matching schema, leaves others untouched
 *
 * ## Two orders of schema — what a `data:` declaration may describe
 *
 * A component's `data:` is 1st order: a DEVELOPER says what shape the section
 * consumes. An authored form (```yaml:form```) is 2nd order: an AUTHOR says what
 * shape a VISITOR will submit. It is schema-shaped, but it is content.
 *
 * Declaring a schema for such a tag is legitimate, and it is worth being precise
 * about what it may describe:
 *
 *   OK    the DEFINITION's envelope — `title?`, `description?`, `fields: <map>`.
 *         That asks "is this a well-formed form?", which is what build-time
 *         validation is for (`build/src/validate-data.js` pairs a section's data
 *         input with the schema its meta.js binds to that key).
 *   WRONG a schema whose fields are THE FORM'S fields (`name`, `email`, …).
 *         Those are author-chosen and unknowable at build time. A form-rendering
 *         component receives its fields; it does not declare them.
 *
 * The mechanism is bounded and does not punish the mistake loudly:
 * `applySchemaToObject` recurses only where the schema declares structure
 * (`type: object` + `fields`, `type: array` + `items.fields`), so a
 * form-definition schema — which cannot name the author's fields — can never
 * reach into them. It fills the envelope defaults its own author declared.
 *
 * (Established with the editor team, 2026-07-31, channel frontend↔framework.
 * The editor shadows a foundation's `form` declaration with its own builder via
 * `builtinSchemas()`; that is about the EDITING UI and is orthogonal to whether a
 * foundation declares a schema for validation.)
 *
 * @param {Object} data - The data object from content
 * @param {Object} schemas - Schema definitions from runtime meta
 * @returns {Object} Data with schemas applied
 */
export function applySchemas(data, schemas) {
  if (!schemas || !data || typeof data !== 'object') {
    return data || {}
  }

  const result = { ...data }

  for (const [tag, rawValue] of Object.entries(data)) {
    const schema = schemas[tag]
    if (!schema) continue  // No schema for this tag - leave as-is

    result[tag] = isRichSchema(schema)
      ? applyRichSchemaToValue(rawValue, schema)
      : applySchemaToValue(rawValue, schema)
  }

  return result
}

/**
 * Apply param defaults from runtime schema
 *
 * @param {Object} params - Params from frontmatter
 * @param {Object} defaults - Default values from runtime schema
 * @returns {Object} Merged params with defaults applied
 */
export function applyDefaults(params, defaults) {
  if (!defaults || Object.keys(defaults).length === 0) {
    return params || {}
  }

  return {
    ...defaults,
    ...(params || {}),
  }
}

/**
 * ⭐ `content.data` HOLDS THE KEYS THE COMPONENT DECLARES, AND NOTHING ELSE — ruled 2026-09-14
 * [Diego]. Every key in its `meta.js` `data:` and its foundation's `main.js` `data:`
 * (`website.declaredKeys`), each filled, in order, from:
 *
 *   1. what the section holds under that key (`block.heldData`) — a tagged data block, or
 *      its own fetch's answer a static build prerendered into it;
 *   2. what the entity store delivered for it — the fetch that fills it (`fillDeclaredKeys`);
 *   3. `null` — nothing fills it, or its fetch is still out (`block.dataLoading`) or failed
 *      (`block.dataError[key]`). ⭐ Never `[]`, which is an answer with no records.
 *
 * A tagged data block under a key the component does not declare is left out, and said so
 * in dev (`warnUndeclaredBlocks`); it stays in `content.sequence`. The data object is
 * rebuilt on each render from those sources, never from the last render's.
 *
 * ⛔ Until 2026-09-14 this merged every delivered key into what the section held, so a
 * section received every fetch that reached it and every tagged block it had.
 */
function assembleData(block, meta, entityData) {
  const declared = block.website?.declaredKeys ? block.website.declaredKeys(meta) : []
  const held = block.heldData || {}
  const data = {}
  for (const [key] of declared) {
    if (held[key] !== undefined) data[key] = held[key]
    else if (entityData && entityData[key] !== undefined) data[key] = entityData[key]
    else data[key] = null
  }
  warnUndeclaredBlocks(block, declared)
  block.parsedContent.data = data
}

/**
 * Say once, in dev, that a tagged data block did not reach its section's component — its
 * key is not declared. The block is still in `content.sequence`, where a component that
 * renders the sequence finds it.
 */
const warnedBlocks = new Set()
function warnUndeclaredBlocks(block, declared) {
  if (!block.website?.entityStore?.dev) return
  const keys = new Set(declared.map(([key]) => key))
  for (const item of block.parsedContent?.sequence || []) {
    if (item?.type !== 'dataBlock' || typeof item.tag !== 'string' || keys.has(item.tag)) continue
    const memo = `${block.type}::${item.tag}`
    if (warnedBlocks.has(memo)) continue
    warnedBlocks.add(memo)
    console.warn(
      `[uniweb] ${block.type}: the \`${item.tag}\` data block is not in content.data — ${block.type} does not ` +
        `declare \`${item.tag}\` in its meta.js \`data:\`. It stays in content.sequence.`
    )
  }
}

/**
 * Run the foundation-level data handler on a block, if one is
 * registered. Runs after entity data merge and before the content
 * handler — the handler sees the fully assembled data and can filter,
 * reshape, or augment it before Loom (or any content transform) runs.
 *
 * The handler receives `(data, block)` where data is
 * `block.parsedContent.data`. It returns a new data object, or
 * null/undefined for no change. The returned data replaces
 * `block.parsedContent.data` for all downstream processing — both
 * the content handler and the component see the transformed data.
 *
 * Skipped when the block is still waiting on async data
 * (`block.dataLoading`), or when no handler is registered.
 * Errors are logged and the original data is preserved.
 */
function runDataHandler(block) {
  if (block.dataLoading) return
  const handler = globalThis.uniweb?.foundationConfig?.handlers?.data
  if (typeof handler !== 'function') return

  try {
    const result = handler(block.parsedContent.data, block)
    if (result != null && result !== block.parsedContent.data) {
      block.parsedContent.data = result
    }
  } catch (err) {
    console.error('Foundation data handler failed:', err)
  }
}

/**
 * Run the foundation-level content handler on a block, if one is
 * registered. Runs at prop-preparation time — after the data handler
 * has had a chance to filter/reshape the data — so the handler sees
 * the fully assembled (and possibly filtered) data. Replaces
 * `block.parsedContent` in place with the re-parsed, instantiated
 * form. The handler receives `(data, block)` and reads raw
 * ProseMirror from `block.rawContent`.
 *
 * Skipped when the block is still waiting on async data
 * (`block.dataLoading`), when no handler is registered, when the
 * block has no raw content, when the handler returns a no-change
 * signal (undefined, null, or the same reference as rawContent), or
 * when the handler throws. Errors are logged via `console.error`.
 */
function runContentHandler(block) {
  if (block.dataLoading) return
  const handler = globalThis.uniweb?.foundationConfig?.handlers?.content
  if (typeof handler !== 'function') return
  if (!block.rawContent || Object.keys(block.rawContent).length === 0) return

  try {
    const transformed = handler(block.parsedContent.data, block)
    if (!transformed || transformed === block.rawContent) return
    const reparsed = block.parseContent(transformed)
    reparsed.data = block.parsedContent.data
    block.parsedContent = reparsed
    block.items = reparsed.items || []
  } catch (err) {
    console.error('Foundation content handler failed:', err)
  }
}

/**
 * Run the foundation-level props handler on the final { content, params }
 * before they reach the component. Runs after content parsing, param
 * defaults, content guarantees, and schema application — the handler
 * sees the exact shape the component would receive and can modify it.
 *
 * The handler receives `(content, params, block)` and returns a new
 * `{ content, params }` object, or null/undefined for no change.
 *
 * Use cases: post-parse content reshaping, computed fields derived
 * from both content and params, param-driven content reorganization.
 * Errors are logged and the original props are preserved.
 */
function runPropsHandler(content, params, block) {
  const handler = globalThis.uniweb?.foundationConfig?.handlers?.props
  if (typeof handler !== 'function') return null

  try {
    const result = handler(content, params, block)
    if (result && typeof result === 'object') return result
  } catch (err) {
    console.error('Foundation props handler failed:', err)
  }
  return null
}

/**
 * Prepare props for a component with runtime guarantees.
 *
 * Does the full content-assembly pipeline in one place so both
 * renderers (`BlockRenderer.jsx` CSR and `ssr-renderer.js` SSG) share
 * the same code path:
 *
 *   1. Assemble `block.parsedContent.data` from the keys the component
 *      declares: what the section holds, else what EntityStore delivered,
 *      else `null` (`assembleData`).
 *   2. Run the foundation data handler (if registered) to filter or
 *      reshape the assembled data.
 *   3. Run the foundation content handler (if registered) on the
 *      block. This may replace `block.parsedContent` with a re-parsed,
 *      instantiated version.
 *   4. Apply param defaults from meta.
 *   5. Build the guaranteed content structure.
 *   6. Apply schemas to content.data.
 *   7. Run the foundation props handler (if registered) for
 *      post-processing of the final { content, params }.
 *
 * Steps 1–3 mutate the block (vanilla JS layer). Steps 4–7 are
 * pure derivations of the block's now-assembled state.
 *
 * @param {Object} block - The block instance
 * @param {Object} meta - Runtime metadata for the component (from meta[componentName])
 * @param {Object|null} [entityData] - Entity data resolved by EntityStore (null if none)
 * @returns {Object} Prepared props: { content, params }
 */
export function prepareProps(block, meta, entityData = null) {
  assembleData(block, meta, entityData)
  // ⭐ A list the section held before the store answered — its own fetch, prerendered
  // into its content by a static build — gets its records' `$route` by the store's rule,
  // which the store did not deliver it by (2026-09-14).
  block.website?.entityStore?.linkOwnRecords?.(block)
  runDataHandler(block)
  runContentHandler(block)

  // Apply param defaults
  const defaults = meta?.defaults || {}
  const params = applyDefaults(block.properties, defaults)

  // Guarantee content structure
  let content = guaranteeContentStructure(block.parsedContent)

  // Apply schemas to content.data
  const schemas = meta?.schemas || null
  if (schemas && content.data) {
    content.data = applySchemas(content.data, schemas)
  }

  // Post-process hook
  const adjusted = runPropsHandler(content, params, block)
  if (adjusted) {
    return {
      content: adjusted.content || content,
      params: adjusted.params || params,
    }
  }

  return { content, params }
}

/**
 * Get runtime metadata for a component from the global uniweb instance
 *
 * @param {string} componentName
 * @returns {Object|null}
 */
export function getComponentMeta(componentName) {
  return globalThis.uniweb?.getComponentMeta?.(componentName) || null
}

/**
 * Get default param values for a component
 *
 * @param {string} componentName
 * @returns {Object}
 */
export function getComponentDefaults(componentName) {
  return globalThis.uniweb?.getComponentDefaults?.(componentName) || {}
}
